import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { ChevronRight, LayoutGrid, PackageOpen, Star } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { getAppsByPermissions, hubApps } from '../config/apps';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { hasModuleViewAccess } from '../lib/module-permissions';
import { cn } from '../lib/utils';
import type { HubApp } from '../types';

const FAVORITES_STORAGE_KEY = 'ophelos-hub-favorite-apps';

/**
 * Get favorite app IDs from localStorage
 */
function getFavoriteAppIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      const ids = JSON.parse(stored) as string[];
      return new Set(ids);
    }
  } catch {
    // localStorage can throw in private browsing mode
  }
  return new Set();
}

/**
 * Save favorite app IDs to localStorage
 */
function saveFavoriteAppIds(ids: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    // localStorage can throw in private browsing mode
  }
}

function AppCard({
  app,
  isFavorite,
  onFavoriteToggle,
  canAccessComingSoon,
}: {
  app: HubApp;
  isFavorite: boolean;
  onFavoriteToggle: (appId: string) => void;
  canAccessComingSoon: boolean;
}) {
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFavoriteToggle(app.id);
  };

  const isComingSoonDisabled = app.comingSoon && !canAccessComingSoon;
  const isDisabled = isComingSoonDisabled;

  const handleClick = (e: React.MouseEvent) => {
    if (isComingSoonDisabled) {
      e.preventDefault();
    }
  };

  return (
    <Link
      to={`/app${app.route}`}
      onClick={handleClick}
      className={`flex-1 group stack z-1 outline-none ${isDisabled ? 'cursor-default' : 'cursor-pointer '}`}
    >
      <div
        className={`border h-full rounded-xl min-h-32 lg:min-h-44 stack bg-card ${
          isDisabled ? 'bg-muted p-0.5 border-border-light' : 'bg-card p-0 border-transparent'
        }`}
      >
        {isDisabled && <span className="mx-auto mb-1 mt-0.5 text-muted-foreground text-xs font-medium">Coming Soon</span>}
        <div
          className={`relative block overflow-hidden bg-card transition py-4 px-6 flex-1 border rounded-xl ${
            isDisabled
              ? ''
              : 'hover:shadow-lg/3 bg-linear-to-l hover:from-accent dark:hover:from-accent/50 hover:to-accent/25 hover:border-ring/40 dark:hover:border-ring/80'
          }`}
        >
          <div className="z-10 relative flex items-start justify-between">
            <div className="flex gap-4 items-center">
              <div className="text-lg font-medium">{app.name}</div>
              {!isDisabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={handleFavoriteClick}
                      size="icon-xs"
                      variant="outline"
                      className={`z-10 px-0.5 py-0.5 size-6 rounded-md duration-300 hover:[&_svg]:fill-yellow-400 hover:[&_svg]:text-yellow-500
                        opacity-0 group-hover:opacity-100 focus:opacity-100`}
                      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Star
                        className={cn(
                          'size-3.5 transition-colors duration-200',
                          isFavorite ? 'fill-yellow-400 text-yellow-500' : 'text-muted-foreground'
                        )}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="whitespace-nowrap">
                    <p>{isFavorite ? 'Remove from favorites' : 'Add to favorites'}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {!isDisabled && (
              <Button
                variant="outline"
                size="icon-xs"
                className="rounded-full border-transparent group-hover:border-ring group-hover:translate-x-2 bg-soft group-hover:bg-card transition shadow-none size-7"
              >
                <ChevronRight />
              </Button>
            )}
          </div>
          <div className="z-10 relative text-base text-muted-foreground pr-6">{app.description}</div>

          <div
            className={`z-0 absolute -bottom-12 -right-12 grayscale opacity-20 ${
              !isDisabled
                ? 'group-hover:text-primary/80 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-120 group-hover:-rotate-3 transition-transform duration-400'
                : ''
            }`}
          >
            <div className="p-6 border border-ring/10 rounded-[3.5rem] group-hover:bg-accent/20">
              <div className="p-5 border border-ring/20 rounded-4xl shadow-lg shadow-ring/10 group-hover:bg-accent/35">
                <div className="p-4 border border-ring/40 rounded-3xl shadow-lg shadow-ring/20 group-hover:bg-card/75">
                  <app.icon className="size-8 text-muted-100" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function AppsPage() {
  const [favoriteAppIds, setFavoriteAppIds] = useState<Set<string>>(() => getFavoriteAppIds());
  const { hasPermission, isLoading } = useUserPermissions();

  const handleFavoriteToggle = (appId: string) => {
    setFavoriteAppIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(appId)) {
        newSet.delete(appId);
      } else {
        newSet.add(appId);
      }
      saveFavoriteAppIds(newSet);
      return newSet;
    });
  };

  // Check if user can access "coming soon" apps (system admins auto-pass via hasPermission)
  const canAccessComingSoon = hasModuleViewAccess(hasPermission, 'coming-soon');

  // Filter apps based on user permissions from WorkOS JWT
  // System admins automatically pass all permission checks
  const allowedApps = useMemo(() => {
    const allowedAppConfigs = getAppsByPermissions(hasPermission);
    const allowedAppIds = new Set(allowedAppConfigs.map((app) => app.id));
    return hubApps.filter((app) => allowedAppIds.has(app.id));
  }, [hasPermission]);

  // Separate apps into favorites and non-favorites
  const favoriteApps = allowedApps.filter((app) => favoriteAppIds.has(app.id));
  const allApps = allowedApps.filter((app) => !favoriteAppIds.has(app.id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader text="Loading apps..." />
      </div>
    );
  }

  if (allowedApps.length === 0) {
    return (
      <div className="flex h-(--page-main-height) items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="subtle">
                <PackageOpen />
              </EmptyMedia>
              <EmptyTitle>No apps available</EmptyTitle>
              <EmptyDescription>
                You don't have access to any apps yet. Ask your administrator to grant you the permissions you need.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background @container mx-auto max-w-[1640px] px-8 mt-[5svh] gap-4 lg:gap-6 2xl:gap-8 2xl:px-20 flex flex-col justify-between">
      {/* Favorites Section */}
      {favoriteApps.length > 0 && (
        <section className="h-full flex-1 w-full">
          <div className="mb-4 flex ml-2 gap-2 items-center">
            <Star className="size-5 text-yellow-500 fill-yellow-500" />
            <h2 className="text-xl font-medium">Favorites</h2>
          </div>
          <div className="grid grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3 gap-6 xl:gap-x-6 xl:gap-y-8 2xl:gap-y-10">
            {favoriteApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                isFavorite={true}
                onFavoriteToggle={handleFavoriteToggle}
                canAccessComingSoon={canAccessComingSoon}
              />
            ))}
          </div>
        </section>
      )}

      {/* All Apps Section */}
      {allApps.length > 0 && (
        <section className="h-full flex-1 w-full">
          <div className="mb-4 flex ml-2 gap-2 items-center">
            <LayoutGrid className="size-5 text-muted-foreground" />
            <h2 className="text-xl font-medium">All Apps</h2>
          </div>

          <div className="grid grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3 gap-6 xl:gap-x-6 xl:gap-y-8 2xl:gap-y-10">
            {allApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                isFavorite={false}
                onFavoriteToggle={handleFavoriteToggle}
                canAccessComingSoon={canAccessComingSoon}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
